import type { S3UploadItem } from "@perceptron/api/apiContracts/external";

export interface UploadFileToS3Options {
  /** Receives byte progress as the upload streams. Total is 0 when not computable. */
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Uploads a file to S3 using a pre-signed URL. Implemented with XMLHttpRequest
 * so callers can observe upload progress events (fetch does not expose
 * upload-side progress).
 *
 * @param uploadUrl - The pre-signed URL
 * @param file - The file to upload
 * @param options - Optional progress callback
 * @throws Error if upload fails
 */
export const uploadFileToS3 = (uploadUrl: string, file: File, options?: UploadFileToS3Options): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);

    if (options?.onProgress) {
      xhr.upload.onprogress = (event) => {
        options.onProgress!(event.loaded, event.lengthComputable ? event.total : 0);
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Failed to upload to S3: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.onabort = () => reject(new Error("Upload aborted"));

    xhr.send(file);
  });
};

/**
 * Uploads multiple files to S3 using an array of upload items
 * @param uploadItems - Array of upload items, each containing an uploadUrl and file
 * @returns Array of successful upload results
 */
export const uploadFilesToS3 = async (uploadItems: S3UploadItem[]): Promise<boolean[]> => {
  const uploadPromises = uploadItems.map(async ({ uploadUrl, file }, index) => {
    try {
      await uploadFileToS3(uploadUrl, file);
      return true;
    } catch (error) {
      console.error(`Failed to upload file ${index}:`, error);
      return false;
    }
  });

  return Promise.all(uploadPromises);
};
